/* eslint-disable no-await-in-loop */

import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useRef, useState } from 'react';

import { UniqueFilenameManager } from './utils/unique-filename-manager';
import { useAdminTRPC } from '../../../shared/providers/trpc';

interface DownloadZipOptions {
  filename?: string;
  /**
   * Max number of files per generated zip. Large selections are split into multiple zips so a single
   * archive never exhausts the tab's memory. Defaults to 100.
   */
  batchSize?: number;
  /**
   * Max accumulated (uncompressed) size in bytes per generated zip. A new zip is started whenever
   * adding the next file would exceed either this or `batchSize`, so a handful of large files can't
   * balloon a single archive's footprint. Defaults to 500 MB.
   */
  maxBatchBytes?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const DEFAULT_BATCH_SIZE = 100;
const DEFAULT_MAX_BATCH_BYTES = 500 * 1024 * 1024;

export function useDownloadZip() {
  const { useMutation } = useAdminTRPC();
  const { mutateAsync } = useMutation('files.downloadFile');
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const isCanceled = useRef(false);
  const analytics = useAnalytics();

  return {
    isDownloading,
    progress,
    cancel: () => {
      isCanceled.current = true;
      setProgress(0);
      setIsDownloading(false);
    },
    downloadZip: async (fileUuids: string[], options: DownloadZipOptions = {}) => {
      // eslint-disable-next-line no-void
      void analytics.track({
        event: 'File - download',
        properties: {
          fileUuids,
        },
      });

      isCanceled.current = false;
      setIsDownloading(true);
      setProgress(0);

      // Files are accumulated into a zip until adding the next one would exceed either the file-count
      // cap or the accumulated-size cap, at which point the current zip is finalized/downloaded and a
      // fresh one is started. A single zip for hundreds of (or a few very large) files exhausts the
      // tab's memory (all input blobs + the generated chunks are held at once), which crashes the
      // page; bounding both count and size keeps each archive's footprint small. The trade-off is
      // that large selections download as multiple `<name> part N.zip` files.
      //
      // The size cap can only be enforced after each file is fetched (that's when its size is known),
      // so the split is dynamic and the total part count isn't known up front. We therefore save each
      // zip as soon as it fills up (keeping only one batch's worth in memory) and number parts
      // sequentially rather than as "x of y".
      const batchSize = options.batchSize ?? DEFAULT_BATCH_SIZE;
      const maxBatchBytes = options.maxBatchBytes ?? DEFAULT_MAX_BATCH_BYTES;
      const totalFiles = fileUuids.length;
      const baseFilename = (options.filename ?? 'download.zip').replace(/\.zip$/i, '');

      let zip = new JSZip();
      // Filenames only need to be unique within a single zip, so this resets alongside `zip`.
      let uniqueFilenameManager = UniqueFilenameManager.init();
      let batchCount = 0;
      let batchBytes = 0;
      let partNumber = 0;

      // Assemble the archive from streamed chunks instead of `generateAsync({ type: 'blob' })`.
      // The latter concatenates every chunk into a single contiguous ArrayBuffer, which throws
      // `RangeError: Array buffer allocation failed`. Building a Blob from an array of chunks lets
      // the browser aggregate multiple backing buffers without a single contiguous allocation.
      const finalizeZip = async (currentZip: JSZip, plainNameAllowed: boolean) => {
        const chunks: BlobPart[] = [];
        await new Promise<void>((resolve, reject) => {
          currentZip
            .generateInternalStream({ type: 'uint8array', streamFiles: true })
            // A Uint8Array is a valid BlobPart at runtime; the cast only bridges TS's
            // `Uint8Array<ArrayBufferLike>` vs `BlobPart` (ArrayBuffer-backed) generic mismatch.
            .on('data', (chunk) => chunks.push(chunk as BlobPart))
            .on('error', reject)
            .on('end', () => resolve())
            .resume();
        });

        const blob = new Blob(chunks, { type: 'application/zip' });
        partNumber += 1;
        // Plain name only when this is the sole part; any earlier flush means there are multiple.
        const filename =
          plainNameAllowed && partNumber === 1 ? `${baseFilename}.zip` : `${baseFilename} part ${partNumber}.zip`;
        saveAs(blob, filename);
      };

      for (let index = 0; index < totalFiles; index += 1) {
        const fileUuid = fileUuids[index];

        if (isCanceled.current) {
          setProgress(0);
          options.onCancel?.();
          return;
        }

        const { downloadUrl, file } = await mutateAsync({
          fileUuid,
          preview: false,
        });

        if (downloadUrl) {
          try {
            const fileContents = await fetch(downloadUrl);
            const blob = await fileContents.blob();

            if (file.filename) {
              // Flush the current (non-empty) zip before adding a file that would push it over either
              // cap. A single file larger than `maxBatchBytes` still lands in its own zip since we
              // only flush when the current one already holds something.
              if (batchCount > 0 && (batchCount >= batchSize || batchBytes + blob.size > maxBatchBytes)) {
                await finalizeZip(zip, false);
                zip = new JSZip();
                uniqueFilenameManager = UniqueFilenameManager.init();
                batchCount = 0;
                batchBytes = 0;
              }

              zip.file(uniqueFilenameManager.getUniqueFilename(file.filename.replaceAll('/', '')), blob, {
                createFolders: false,
                dir: false,
              });
              batchCount += 1;
              batchBytes += blob.size;
            }
          } catch (err) {
            // invalid/not found file, skipping
          }
        }

        // sets progress percentage based on files processed
        setProgress(Math.floor(((index + 1) / totalFiles) * 100));
      }

      if (isCanceled.current) {
        setProgress(0);
        options.onCancel?.();
        return;
      }

      if (batchCount > 0) {
        await finalizeZip(zip, true);
      }

      setIsDownloading(false);
      setProgress(0);
      options.onSuccess?.();
    },
  };
}
